import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import imageCompression from "browser-image-compression";
import { PDFDocument } from 'pdf-lib';

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function cleanBio(bio) {
  if (!bio) return "";
  const instaRegex = /^https?:\/\/(www\.)?instagram\.com\/[^\s]+\/?\s*/i;
  return bio.replace(instaRegex, "").trim();
}

export function debounce(func, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}
export const toArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

export const parseValuesToLabels = (incomingSkills) => {
  const skillOptions = [
    { value: "13", label: "Director" },
    { value: "12", label: "Livestream Audio" },
    { value: "2", label: "Video Event" },
    { value: "3", label: "Video Music" },
    { value: "4", label: "Video Lifestyle" },
    { value: "5", label: "Photo Portrait" },
    { value: "6", label: "Photo Product" },
    { value: "7", label: "Photo Event" },
    { value: "8", label: "Photo Lifestyle" },
    { value: "9", label: "Audio Engineer" },
    { value: "10", label: "Creative Director" },
    { value: "11", label: "Livestream Director" },
    { value: "1", label: "Video Commercial" },
    { value: "14", label: "Video Weddings" },
    { value: "15", label: "Photo Weddings" },
    { value: "16", label: "Portrait Photo" },
    { value: "17", label: "Cinematographer" },
  ];

  if (!incomingSkills) return [];

  let parsed = incomingSkills;

  try {
    // If it's a string, CLEAN it first
    if (typeof parsed === "string") {
      parsed = parsed
        .replace(/\\+"/g, '"')        // remove escaped quotes: \" → "
        .replace(/[\u201C\u201D]/g, '"') // fix fancy quotes
        .replace(/^\s+|\s+$/g, "");   // trim

      parsed = JSON.parse(parsed);
    }

    // Ensure array
    if (!Array.isArray(parsed)) return [];

    parsed = parsed.map((v) => Number(v));
  } catch (e) {
    console.error("❌ Failed to parse skills:", e);
    return [];
  }

  // Map to labels
  return parsed
    .map((val) => {
      const option = skillOptions.find((s) => Number(s.value) === val);
      return option?.label;
    })
    .filter(Boolean);
};

export const calculateDuration = (startTime, endTime) => {
  const formatTwoDigits = (num) => String(num).padStart(2, "0");

  if (!startTime || !endTime) {
    return {
      hours: 1,
      formatted: "01h 00m",
    };
  }

  const toMinutes = (timeStr) => {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);

    return hour * 60 + minute;
  };

  const start = toMinutes(startTime);
  const end = toMinutes(endTime);

  if (start == null || end == null) {
    return {
      hours: 1,
      formatted: "01h 00m",
    };
  }

  let duration = end - start;
  if (duration < 0) duration += 24 * 60;

  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;

  const roundedHours = Math.round(duration / 60);

  return {
    hours: roundedHours,
    formatted: `${formatTwoDigits(hours)}h ${formatTwoDigits(minutes)}m`,
  };
};

/**
 * Compresses an image file for upload.
 * @param file The original File object from the user.
 * @param settings Optional overrides for size and dimensions.
 * @returns A promise resolving to the compressed File object.
 */
export async function compressImage(file, settings = {}) {
  const options = {
    maxSizeMB: settings.maxSizeMB ?? 1,
    maxWidthOrHeight: settings.maxWidthOrHeight ?? 1920,
    useWebWorker: true,
    onIteration: (iteration) =>
      console.log(`Compression iteration: ${iteration}`),
  };

  try {
    const compressedBlob = await imageCompression(file, options);

    return new File([compressedBlob], file.name, {
      type: file.type,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Image compression failed:", error);
    return file; // Fallback to original file if compression fails
  }
}

/**
 * Compresses a PDF file using pdf-lib features.
 * @param {File} file - The original PDF file.
 * @returns {Promise<File>} - The compressed PDF file or original on error.
 */
export async function compressPDF(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();

    const pdfDoc = await PDFDocument.load(arrayBuffer);

    // Save with optimization settings:
    // useObjectStreams: Groups objects into streams for smaller file size
    const compressedBytes = await pdfDoc.save({
      useObjectStreams: true,
      addDefaultPage: false
    });

    const blobPart = new Uint8Array(compressedBytes);

    return new File([blobPart], file.name, { type: 'application/pdf' });
  } catch (error) {
    console.error("PDF compression failed, returning original file:", error);
    return file; // Fallback to original file if compression fails
  }
}
